// Axios setup. Responses are camelized at runtime by the response interceptor
// below, so callers should annotate response types with the camelCase aliases
// exported from @/types/api-types — never the raw snake_case
// components["schemas"]["…"] form.
import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios"
import humps from "humps"

import { logout } from "@/actions/auth"
import { store } from "@/lib/store"
import { API_ROOT_URL, SELF_RECORDING_APP_URL } from "@/requests"

export const init = (): void => {
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.data && !(response.data instanceof Blob)) {
        response.data = humps.camelizeKeys(response.data)
      }
      return response
    },
    (error: AxiosError) => {
      const { response } = error
      if (response?.data && !(response.data instanceof Blob)) {
        response.data = humps.camelizeKeys(response.data)
      }
      if (error.config?.url) {
        const url = new URL(error.config.url)
        const isOurBackendResponse = url.origin === API_ROOT_URL
        const requestHadToken = Boolean(error.config.headers?.Authorization)
        const status = response?.status
        const isAuthFailure = status === 401 || status === 403
        if (isAuthFailure && isOurBackendResponse && requestHadToken) {
          console.log(`${status} error on ${url.origin}, clearing localStorage`)
          store.dispatch(logout())
        }
      }
      return Promise.reject(error)
    },
  )

  axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const shouldAuthenticateRequest =
      config.url?.startsWith(API_ROOT_URL) || config.url?.startsWith(SELF_RECORDING_APP_URL)

    if (shouldAuthenticateRequest) {
      if (config.data) {
        config.data = humps.decamelizeKeys(config.data)
      }
      const token = window.localStorage.getItem("token")
      if (token) {
        config.headers.Authorization = token
      }
    }
    return config
  })
}

export const queryString = (params: Record<string, string>): string => {
  return new URLSearchParams(humps.decamelizeKeys(params) as Record<string, string>).toString()
}
